import { useCallback, useMemo, useState } from 'react';
import { FormikConfig, FormikHelpers } from 'formik';
import { createContext, useContext } from 'use-context-selector';
import * as Yup from 'yup';

import { INITIAL_VALUES } from './constant';
import { IFormCreationValues } from './types';

import { SUCCESS_DIALOG, USER_ROLE } from '~common';
import { DEFAULT_OPENING_TIMINGS } from '~configs';
import { useModal } from '~hooks/useModal';
import { useCreatePinningRequestMutation } from '~hooks/useMutation';
import {
  PINNING_STEP,
  usePinningStepContext,
} from '~providers/PinningStepProvider';
import { useVenuePlacementCreationContext } from '~providers/VenuePlacementCreationProvider';
import {
  ICreatePinningRequestParams,
  IDropStationRequestParams,
  IRelocateStationRequestParams,
  ISwapStationRequestParams,
} from '~types';
import {
  checkRolesMatchWithPrimaryUserRole,
  isValidPhoneNumber,
  sanitize,
  toastError,
} from '~utils';

const genVenueErrorMessage = (interpolateMessage: string) =>
  `Venue address's ${interpolateMessage} is required`;

const genPlacementErrorMessage = (interpolateMessage: string) =>
  `Placement's ${interpolateMessage} is required`;

const addressSchema = Yup.object({
  name: Yup.string().required("Venue's address is required"),
  location: Yup.object({
    lat: Yup.number().required(genVenueErrorMessage('latitude')),
    lng: Yup.number().required(genVenueErrorMessage('longitude')),
    infoAddress: Yup.object({
      city: Yup.string().required(genVenueErrorMessage('city')),
      state: Yup.string().required(genVenueErrorMessage('state')),
      zip: Yup.string().required(genVenueErrorMessage('zip')),
      country: Yup.string().required(genVenueErrorMessage('country')),
      countryCode: Yup.string().required(genVenueErrorMessage('country code')),
    }),
  }),
});

const venueSchema = Yup.object({
  locationName: Yup.string().required('Venue name is required'),
  address: addressSchema,
  venueType: Yup.object().required('Venue Type is required'),
  venueSubType: Yup.object().nullable(),
  venueContact: Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Email is invalid').required('Email is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .test('phone-number-valid', 'Phone number is invalid', (value) =>
        isValidPhoneNumber(value),
      ),
    role: Yup.string().required('Title / Role is required'),
  }),
});

const getNewVenueSchema = (contactSkippedStep?: PINNING_STEP) =>
  contactSkippedStep ? venueSchema.omit(['venueContact']) : venueSchema;

const placementSchema = Yup.object({
  name: Yup.string().required('Placement name is required'),
  placementType: Yup.object().required('Placement type is required'),
  distributionChannel: Yup.object()
    .nullable()
    .test(
      'distribution-channel-valid',
      'Distribution channel is required',
      (value, _) => {
        if (
          checkRolesMatchWithPrimaryUserRole([
            USER_ROLE.DISTRIBUTOR,
            USER_ROLE.LIMITED_DISTRIBUTOR,
            USER_ROLE.PARTNER,
          ])
        ) {
          return true;
        }
        return value != null;
      },
    ),
});

const stationSchema = Yup.object({
  station: Yup.object().required('Station is required'),
  location: Yup.object({
    latitude: Yup.number().required(genPlacementErrorMessage('latitude')),
    longitude: Yup.number().required(genPlacementErrorMessage('longitude')),
  }),
  attachment: Yup.object({
    imei: Yup.string().required('IMEI is required'),
    fullStationScreen: Yup.string().required('Full Station Screen is required'),
    stationLeftSide: Yup.string().required('Station Left Side is required'),
    stationRightSide: Yup.string().required('Station Right Side is required'),
    stationFrontSide: Yup.string().required('Station Front Side is required'),
    stationPerspective: Yup.string().required(
      'Station Perspective is required',
    ),
    additional: Yup.string(),
  }),
});

const initialContextValues = {
  initialValues: {} as IFormCreationValues,
  formResetKey: 0,
  handleSetInitialValues: (_values: IFormCreationValues) => {
    return;
  },
  handleSubmit: (
    _: IFormCreationValues,
    _formikHelpers: FormikHelpers<IFormCreationValues>,
  ) => {
    return;
  },
  isFormSubmitSuccessDialogOpen: false,
  closeFormSubmitSuccessDialog: () => {
    return;
  },
  validationSchema: Yup.object({}),
  successDialogContent: '',
  isFormSubmitLoading: false,
};

const FormCreateContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const FormCreateProvider: React.FC<Props> = ({ children }) => {
  const {
    existingVenueDetail,
    placementListInfo,
    handleReset,
    distributionChannelOptions,
  } = useVenuePlacementCreationContext();
  const {
    selectedVenueId,
    selectedGooglePlaceId,
    handleSetPinningStep,
    selectedPlacementId,
    isRelocationFlow,
    contactSkippedStep,
  } = usePinningStepContext();
  const { isOpen, openModal, closeModal } = useModal(() =>
    handleSetPinningStep(PINNING_STEP.START),
  );
  // Set content of success dialog
  const [content, setContent] = useState<string>('');
  const createPinningRequest = useCreatePinningRequestMutation();

  const [formResetKey, setFormResetKey] = useState(0);

  // Initial values for pinning form
  const [initialValues, setInitialValues] = useState<IFormCreationValues>(
    () => {
      const placementInfoByRole = checkRolesMatchWithPrimaryUserRole([
        USER_ROLE.DISTRIBUTOR,
        USER_ROLE.LIMITED_DISTRIBUTOR,
        USER_ROLE.PARTNER,
      ])
        ? {
            distributionChannel:
              distributionChannelOptions.find(
                (channel) => channel.name.trim().toLowerCase() === 'enterprise',
              ) ?? null,
          }
        : {};

      return {
        venue: INITIAL_VALUES.venue,
        placement: {
          ...INITIAL_VALUES.placement,
          ...placementInfoByRole,
        },
        swapReasonId: INITIAL_VALUES.swapReasonId,
        relocationReasonId: INITIAL_VALUES.relocationReasonId,
        swapNote: INITIAL_VALUES.swapNote,
        relocationNote: INITIAL_VALUES.relocationNote,
      };
    },
  );

  const handleSetInitialValues = useCallback((values: IFormCreationValues) => {
    setInitialValues(values);
    setFormResetKey((k) => k + 1);
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        venue: !selectedVenueId
          ? getNewVenueSchema(contactSkippedStep)
          : Yup.object({}),
        placement: Yup.object({
          ...(selectedPlacementId ? {} : placementSchema.fields),
          ...(isRelocationFlow
            ? {
                location: stationSchema.fields.location,
                attachment: stationSchema.fields.attachment,
              }
            : stationSchema.fields),
        }),
      }),
    [
      contactSkippedStep,
      isRelocationFlow,
      selectedPlacementId,
      selectedVenueId,
    ],
  );

  const handleSubmit: FormikConfig<IFormCreationValues>['onSubmit'] =
    useCallback(
      (values: IFormCreationValues) => {
        const relocationList = placementListInfo.filter(
          (placement) => placement.isRelocationFlow && !!placement.placementId,
        );

        const dropStationList = placementListInfo.filter(
          (placement) =>
            placement.placementId && !placement.isPlacementAssignedStation,
        );

        const swapStationList = placementListInfo.filter(
          (placement) =>
            placement.placementId &&
            placement.isPlacementAssignedStation &&
            !placement.isRelocationFlow,
        );

        const placementCreateList = placementListInfo.filter(
          (placement) => !placement.placementId,
        );

        // Values typed before Skip are kept for Back, not submitted
        const newVenueContact = !contactSkippedStep
          ? values.venue.venueContact
          : undefined;

        const reqParams: ICreatePinningRequestParams = {
          ...(selectedVenueId
            ? { venueId: selectedVenueId }
            : {
                venueRequest: {
                  googlePlaceId: selectedGooglePlaceId,
                  locationName: values.venue.locationName,
                  venueTypeId: values.venue.venueType?.id,
                  venueSubTypeId:
                    values.venue.venueSubType?.id &&
                    values.venue.venueSubType?.name !== 'None'
                      ? values.venue.venueSubType.id
                      : undefined,
                  addressLine1: values.venue.address.name,
                  city: values.venue.address.location.infoAddress.city,
                  state: values.venue.address.location.infoAddress.state,
                  country: values.venue.address.location.infoAddress.country,
                  countryCode:
                    values.venue.address.location.infoAddress.countryCode,
                  zip: values.venue.address.location.infoAddress.zip,
                  venueContact: newVenueContact,
                  // Check out conversation: https://www.figma.com/design/AdbkeQDpn1c6xDVwRlHidI?node-id=2779-49883&m=dev#979331557
                  openingTimings: DEFAULT_OPENING_TIMINGS, // Default value to bypass
                  operatingHours: values.venue.operatingHours,
                },
              }),
          addressRequests: placementCreateList.map((placement) => ({
            placementTypeId: placement.placementType?.id,
            distributionChannelId: placement.distributionChannel?.id,
            locationName: placement.name,
            locationPoint: placement.location,
            locationAttachments: sanitize(placement.attachment),
            stationId: placement.station?.id,
            sameOpeningTimingsAsVenue: true,
            // Bypass values for old flow
            venueTypeId:
              existingVenueDetail?.venueTypeId || values.venue.venueType?.id,
            addressLine1:
              existingVenueDetail?.addressLine1 || values.venue.address.name,
            city:
              existingVenueDetail?.city ||
              values.venue.address.location.infoAddress.city,
            state:
              existingVenueDetail?.state ||
              values.venue.address.location.infoAddress.state,
            country:
              existingVenueDetail?.country ||
              values.venue.address.location.infoAddress.country,
            zip:
              existingVenueDetail?.zip ||
              values.venue.address.location.infoAddress.zip,
            addressContact: selectedVenueId
              ? existingVenueDetail?.venueContact
              : newVenueContact,
          })),
          dropStationRequests: dropStationList
            .map((placement) => {
              if (!placement.placementId) return null; // Explicitly return null for clarity
              return {
                addressId: placement.placementId,
                locationPoint: placement.location,
                locationAttachments: sanitize(placement.attachment),
                stationId: placement.station?.id,
              };
            })
            .filter(
              (placement): placement is IDropStationRequestParams =>
                placement !== null,
            ),
          swapStationRequests: swapStationList
            .map((placement) => {
              if (!placement.placementId) return null;
              return {
                currentStationId: placement.assignedStationId,
                newStationId: placement.station?.id,
                locationAttachments: sanitize(placement.attachment),
                // Per placement, so each swap sends its own reason rather than
                // whatever the last one entered.
                unpinningReasonId: placement.swapReasonId,
                ...(placement.swapNote
                  ? { unpinningNote: placement.swapNote }
                  : {}),
              };
            })
            .filter(
              (placement): placement is ISwapStationRequestParams =>
                placement !== null,
            ),
          relocateStationRequests: relocationList.map(
            (placement): IRelocateStationRequestParams => ({
              addressId: placement.placementId as string,
              locationName: placement.name,
              placementTypeId: placement.placementType?.id,
              distributionChannelId: placement.distributionChannel?.id,
              location: placement.location,
              attachment: sanitize(placement.attachment),
              unpinningReasonId: placement.relocationReasonId ?? '',
              unpinningNote: placement.relocationNote || undefined,
            }),
          ),
        };

        createPinningRequest.mutate(reqParams, {
          onSuccess() {
            setContent(
              reqParams.venueId
                ? SUCCESS_DIALOG.FORM_SUBMISSION.SUBTITLE.EXISTING_VENUE
                : SUCCESS_DIALOG.FORM_SUBMISSION.SUBTITLE.NEW_VENUE,
            );
            handleReset(); // Reset all data after submit
            openModal();
          },
          onError(error) {
            toastError({
              error,
              axiosMessage: 'Something went wrong when submit pinning form!',
            });
          },
        });
      },
      [
        contactSkippedStep,
        createPinningRequest,
        existingVenueDetail?.addressLine1,
        existingVenueDetail?.city,
        existingVenueDetail?.country,
        existingVenueDetail?.state,
        existingVenueDetail?.venueContact,
        existingVenueDetail?.venueTypeId,
        existingVenueDetail?.zip,
        handleReset,
        openModal,
        placementListInfo,
        selectedGooglePlaceId,
        selectedVenueId,
      ],
    );

  const providerValues = useMemo(
    () => ({
      initialValues,
      formResetKey,
      handleSetInitialValues,
      validationSchema,
      handleSubmit,
      isFormSubmitSuccessDialogOpen: isOpen,
      closeFormSubmitSuccessDialog: closeModal,
      successDialogContent: content,
      isFormSubmitLoading: createPinningRequest.isLoading,
    }),
    [
      closeModal,
      content,
      createPinningRequest.isLoading,
      formResetKey,
      handleSubmit,
      initialValues,
      handleSetInitialValues,
      isOpen,
      validationSchema,
    ],
  );

  return (
    <FormCreateContext.Provider value={providerValues}>
      {children}
    </FormCreateContext.Provider>
  );
};

export const useFormCreateContext = () => useContext(FormCreateContext);
